import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import styles from './AudioMeter.module.css';

interface Props {
  /**
   * Returns the current normalized audio level (0..1) in enhanced mode,
   * or null when no analyser is wired (browser STT mode or pre-listen).
   * Polled per animation frame from inside the meter; the hook avoids a
   * React re-render storm by reading a ref each frame.
   */
  getLevel: () => number | null;
  /** Number of bars to render. Defaults to 5 (the inline labelPill / cross-surface size). */
  bars?: number;
  /**
   * Optional inline style pass-through for the meter container. Used by
   * the home-view hero waveform to override width / height and set
   * `--bar-width` / `--bar-gap` CSS custom properties without touching
   * the cross-surface defaults (UpdateSheet × 3 variants, EvidenceBook
   * testimony, PrayerDetail TestimonyModal — all reach AudioMeter
   * through MicButton's labelPill at bars=5 / no style override).
   *
   * Example: `style={{ width: 260, height: 48, '--bar-width': '8px',
   * '--bar-gap': '4px' } as CSSProperties}` — cast required because
   * React.CSSProperties doesn't type CSS custom properties.
   */
  style?: CSSProperties;
}

const DEFAULT_BARS = 5;

/**
 * Per-bar height multipliers — gives the meter a non-uniform "audio-y"
 * shape when driven by live RMS. Same offsets every session so the
 * visual is predictable rather than randomly jittery. Index modulo
 * bars when more or fewer bars are requested.
 */
const BAR_OFFSETS = [0.7, 1.0, 0.85, 1.0, 0.65];

/**
 * Live-mode normalization. Analyser RMS in this app sits typically in
 * 0..0.3 for normal speech, so a gain of ~6 maps "speaking" to the top
 * of the meter without clipping. Floor 0.18 keeps every bar slightly
 * lifted off baseline so the meter always reads as "alive, listening"
 * rather than collapsing to zero between syllables.
 */
const LIVE_FLOOR = 0.18;
const LIVE_GAIN = 6;
const LIVE_CEILING = 1;

/**
 * Visual audio meter that always animates while the mic is open. Two
 * modes selected at runtime per the level returned by the consumer:
 *
 *   - `live` (level >= 0): bar heights driven by the analyser per
 *     animation frame. Used in enhanced mode.
 *   - `synthetic` (level === null): CSS-only idle pulse. Used in
 *     browser STT mode where there's no audio source, and during the
 *     brief window between recorder start and first analyser data.
 *
 * The component never appears blank — the synthetic pulse is the
 * "trust mechanism" for closed-eyes prayer per the locked B.1 design:
 * even when no audio is detected and no haptic fires (iOS), the meter
 * keeps moving so the user perceives the session as alive.
 */
export default function AudioMeter({ getLevel, bars = DEFAULT_BARS, style }: Props) {
  const barRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const rafRef = useRef<number | null>(null);
  // Mode is presentation-only; we keep it in state so the data attribute
  // updates and the CSS synthetic-pulse animation can attach/detach.
  // Most frames don't change mode so re-renders stay rare.
  const [mode, setMode] = useState<'live' | 'synthetic'>('synthetic');

  useEffect(() => {
    let active = true;
    let lastMode: 'live' | 'synthetic' = 'synthetic';

    const tick = () => {
      if (!active) return;
      const level = getLevel();
      const next: 'live' | 'synthetic' = level === null ? 'synthetic' : 'live';
      if (next !== lastMode) {
        lastMode = next;
        setMode(next);
      }

      if (next === 'live' && level !== null) {
        const norm = Math.max(LIVE_FLOOR, Math.min(LIVE_CEILING, level * LIVE_GAIN + LIVE_FLOOR));
        for (let i = 0; i < bars; i++) {
          const el = barRefs.current[i];
          if (!el) continue;
          const offset = BAR_OFFSETS[i % BAR_OFFSETS.length];
          const scale = Math.max(LIVE_FLOOR, Math.min(LIVE_CEILING, norm * offset));
          el.style.setProperty('--bar-scale', String(scale));
        }
      } else {
        // Synthetic mode: clear the inline custom property so the CSS
        // keyframe animation drives bar height instead.
        for (const el of barRefs.current) {
          if (el) el.style.removeProperty('--bar-scale');
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      active = false;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [getLevel, bars]);

  return (
    <span className={styles.meter} data-mode={mode} aria-hidden="true" style={style}>
      {Array.from({ length: bars }).map((_, i) => (
        <span
          key={i}
          ref={(el) => {
            barRefs.current[i] = el;
          }}
          className={styles.bar}
          // Inline animation-delay handles the synthetic-mode phase-
          // offset wave at any bar count (mod 5 cycles the existing
          // 0/200/400/600/800ms pattern). Replaces the bar0..bar4 CSS
          // class lookup that capped the visible-wave behavior at 5
          // bars. Has no effect in live mode (no animation runs).
          style={{ animationDelay: `${(i % 5) * 200}ms` }}
        />
      ))}
    </span>
  );
}
